import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '../../../components/common/Spinner/Spinner';
import WizardDialog from '../../../components/envago/WizardDialog/WizardDialog';
import Button from '../../../components/envago/Button/Button';
import PopConfirmButton from '../../../components/envago/PopConfirmButton/PopConfirmButton';
import { ConfirmContentType } from '../../../components/envago/ConfirmDialog';
import { applicationSelectors } from '../../../redux/application';
import { jobsSelectors, jobsActions } from '../../../redux/jobs';
import { offlineActions } from '../../../redux/offline';
import queueSelectors from '../../../redux/offline/queue.selectors';
import { notificationActions } from '../../../redux/notification';
import { JobType } from '../../../redux/jobs/jobs.types';
import { ImportGroup, ImportRow, ImportStats, ImportWizardStep, ReviewEntry } from './ImportExcel.types';
import {
    buildImportRows,
    canAdvance,
    combineReadingHints,
    computeImportStats,
    countPlausibleGroupRows,
    getRowsWithReadingValueValidationError,
    groupRowsByJob,
    isImportableRow,
    parseExcelBuffer,
    reExportOriginalFiles,
    toMeterTaskResults,
    toStartOfDayISO,
} from './ImportExcel.utils';
import { mergeJobConfig } from '../../../helper/JobConfigHelper';
import { InvalidReadingImagePayloadError } from '../../../helper/ReadingImagePayloadHelper';
import { FileSelectStep } from './steps/FileSelectStep';
import { PlausibleStep } from './steps/PlausibleStep';
import { ImplausibleWarningStep } from './steps/ImplausibleWarningStep';
import { ImplausibleReviewStep } from './steps/ImplausibleReviewStep';
import { SummaryStep } from './steps/SummaryStep';

type Props = {
    visible: boolean;
    onClose: () => void;
};

function buildJobsMap(jobs: JobType[]): Map<string, JobType> {
    return new Map(jobs.map((j) => [j.id, j]));
}

function toWizardStep(step: ImportWizardStep): 0 | 1 | 2 {
    if (step === 'FILE_SELECT' || step === 'ANALYZING') return 0;
    if (step === 'SUMMARY') return 2;
    return 1;
}

// A 'partial' group (job has tasks not present in the file) can still contain
// an implausible row — that row must go through implausible review just like
// a fully 'implausible' group, never get auto-imported or silently skipped.
function needsImplausibleReview(g: ImportGroup): boolean {
    return g.category === 'implausible' || (g.category === 'partial' && g.rows.some((r) => r.category === 'implausible'));
}

const INITIAL_STATS: ImportStats = {
    total: 0,
    submitted: 0,
    submittedPlausible: 0,
    submittedImplausible: 0,
    implausibleSkipped: 0,
    skipped: 0,
    notFound: 0,
    noValue: 0,
    duplicateConflict: 0,
    duplicateExact: 0,
    invalidReadingDate: 0,
    invalidReading: 0,
    dateMismatch: 0,
    expired: 0,
    notYet: 0,
    alreadyDone: 0,
};

export const ImportExcel: React.FC<Props> = ({ visible, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const jobs = useSelector(jobsSelectors.getJobs) as JobType[];
    const applicationConfig = useSelector(applicationSelectors.getApplicationConfig);
    const featuresConfig = useSelector(applicationSelectors.getFeaturesConfig);
    // Local offline send-queue — readings enqueued but not yet backend-confirmed.
    // Needed so a re-import of the same file doesn't queue an already-queued
    // reading a second time (envago/ksa-pro-ui#325); see isReadingAlreadyQueued.
    const queuedActions = useSelector(queueSelectors.getQueuedActions);

    const requireComment = featuresConfig?.unplausibleReadingsCommentRequired ?? false;
    const requirePhoto = featuresConfig?.unplausibleReadingsImageRequired ?? false;

    const [step, setStep] = useState<ImportWizardStep>('FILE_SELECT');
    const [parseError, setParseError] = useState<string | null>(null);
    const [groups, setGroups] = useState<ImportGroup[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [stats, setStats] = useState<ImportStats>(INITIAL_STATS);
    const [reviewQueue, setReviewQueue] = useState<ImportGroup[]>([]);
    const [reviewIndex, setReviewIndex] = useState(0);
    const [currentReview, setCurrentReview] = useState<ReviewEntry | null>(null);
    const [fileSelectTrigger, setFileSelectTrigger] = useState<(() => void) | null>(null);
    const [skippedFileCount, setSkippedFileCount] = useState(0);
    const [fileBuffers, setFileBuffers] = useState<Array<{ buffer: ArrayBuffer; name: string }>>([]);
    const [importedRowsByFile, setImportedRowsByFile] = useState<Map<number, Set<number>>>(new Map());
    const [reExporting, setReExporting] = useState(false);

    const implausibleGroups = groups.filter(needsImplausibleReview);

    const getEffectiveRequirements = useCallback(
        (group: ImportGroup) => {
            const mergedConfig = mergeJobConfig(group.job?.config, applicationConfig);
            const commentsEnabled = mergedConfig?.meterReading?.commentsEnabled ?? true;
            const imagesForbidden = mergedConfig?.features?.imagesForbidden ?? false;
            return {
                requireComment: commentsEnabled ? mergedConfig?.features?.unplausibleReadingsCommentRequired ?? requireComment : false,
                requirePhoto: imagesForbidden ? false : mergedConfig?.features?.unplausibleReadingsImageRequired ?? requirePhoto,
            };
        },
        [applicationConfig, requireComment, requirePhoto],
    );

    // I4: per-job config override for comment/photo requirements
    const jobMergedConfig = useMemo(
        () => (currentReview ? mergeJobConfig(currentReview.group.job?.config, applicationConfig) : undefined),
        [currentReview, applicationConfig],
    );
    const commentsEnabledForGroup = jobMergedConfig?.meterReading?.commentsEnabled ?? true;
    const imagesForbiddenForGroup = jobMergedConfig?.features?.imagesForbidden ?? false;
    const requireCommentForGroup = commentsEnabledForGroup ? jobMergedConfig?.features?.unplausibleReadingsCommentRequired ?? requireComment : false;
    const requirePhotoForGroup = imagesForbiddenForGroup ? false : jobMergedConfig?.features?.unplausibleReadingsImageRequired ?? requirePhoto;
    const canImportReview = currentReview
        ? canAdvance(
              requirePhotoForGroup,
              requireCommentForGroup,
              imagesForbiddenForGroup,
              commentsEnabledForGroup,
              currentReview.images,
              currentReview.comment,
              getRowsWithReadingValueValidationError(currentReview.group)
                  .map((row) => row.taskId)
                  .filter((taskId): taskId is string => !!taskId),
          )
        : false;

    // The current review group mixes a plausible reading in with the
    // implausible one(s) it's actually being reviewed for — offer a
    // "plausible only" fallback instead of blocking the whole group on
    // requirements (photo/comment) that only the implausible reading needs.
    const hasPlausibleRowInReview = currentReview?.group.rows.some((r) => r.category === 'plausible') ?? false;

    const plausibleCount = countPlausibleGroupRows(groups);

    // ─── Queue helper ────────────────────────────────────────────────────────────

    // Marks rows as resolved for re-export purposes (excluded from the
    // re-exported file), regardless of whether they were actually sent to the
    // API — e.g. already-submitted rows are resolved without ever being enqueued.
    const markRowsResolved = (rows: ImportRow[]) => {
        if (rows.length === 0) return;
        setImportedRowsByFile((prev) => {
            const next = new Map(prev);
            for (const row of rows) {
                if (!next.has(row._fileIndex)) next.set(row._fileIndex, new Set());
                next.get(row._fileIndex)!.add(row._rowIndex);
            }
            return next;
        });
    };

    const enqueueRows = (jobId: string, rows: ImportRow[], comment = '', images: Record<string, string[]> = {}) => {
        const dispatchedRows = rows.filter((r) => r.ablesewert !== null && r.ablesedatum && r.taskId && isImportableRow(r));

        // Group by the ACTUAL entered reading date — never collapse to a single
        // shared date. A job can legitimately contain tariffs read at different
        // times (e.g. one tariff scheduled over several future months), and
        // forcing one date onto all of them would submit wrong readings for the
        // others. Tariffs read together on the same visit naturally share one
        // date already, so they still result in a single call.
        const rowsByDate = new Map<string, ImportRow[]>();
        dispatchedRows.forEach((r) => {
            const key = r.ablesedatum!;
            if (!rowsByDate.has(key)) rowsByDate.set(key, []);
            rowsByDate.get(key)!.push(r);
        });

        rowsByDate.forEach((rowsForDate, date) => {
            const taskResults = toMeterTaskResults(rowsForDate, comment, images);
            if (taskResults.length === 0) return;
            dispatch(offlineActions.queueAction(jobsActions.sendMeterTaskResults(jobId, toStartOfDayISO(date)!, taskResults)));
        });

        markRowsResolved(dispatchedRows);
    };

    const enqueueGroup = (group: ImportGroup, comment = '', images: Record<string, string[]> = {}) =>
        enqueueRows(group.jobId, group.rows, comment, images);

    // ─── Handlers ────────────────────────────────────────────────────────────────

    const handleFilesSelected = async (files: File[]) => {
        if (jobs.length === 0) {
            setParseError(t('management.import.fileSelect.noJobsError'));
            return;
        }

        setStep('ANALYZING');
        setParseError(null);
        try {
            const jobsMap = buildJobsMap(jobs);
            const buffers = await Promise.all(files.map(async (f) => ({ buffer: await f.arrayBuffer(), name: f.name })));
            setFileBuffers(buffers);
            // Reuse the buffers we just read (also kept for re-export) instead of reading each file twice.
            const rawRows = (await Promise.all(buffers.map((b, i) => parseExcelBuffer(b.buffer, i)))).flat();
            const rows = buildImportRows(rawRows, jobsMap, applicationConfig, undefined, queuedActions);
            const grouped = groupRowsByJob(rows);
            setGroups(grouped);
            setTotalRows(rawRows.length);

            // Already-submitted rows never go through enqueueGroup (nothing to
            // send), but they ARE resolved — a re-export must not include them
            // again alongside the genuinely unresolved rows.
            markRowsResolved(rows.filter((r) => r.category === 'already_done'));

            const hasPlausible = grouped.some((g) => g.category === 'plausible' && g.rows.some((row) => row.category === 'plausible'));
            const hasImplausible = grouped.some(needsImplausibleReview);

            if (!hasPlausible && !hasImplausible) {
                // Fast-path: auto-import non-strict partial groups, then summary.
                finishSummary(autoImportPartial(grouped), rawRows.length);
            } else if (!hasPlausible) {
                goToImplausible(grouped);
            } else {
                setStep('PLAUSIBLE_STEP');
            }
        } catch (e) {
            // Discard whatever partial state this failed attempt built up
            // (fileBuffers/groups/totalRows/importedRowsByFile) instead of
            // leaving it to bleed into the next attempt.
            resetImportRun();
            setParseError(t('management.import.fileSelect.error'));
            setStep('FILE_SELECT');
        }
    };

    // Marks the given groups (by jobId) with a decision, returning a new array.
    const applyDecision = (
        source: ImportGroup[],
        jobIds: Set<string>,
        decision: 'submitted' | 'skipped',
    ): ImportGroup[] => source.map((g) => (jobIds.has(g.jobId) ? { ...g, decision } : g));

    // Non-strict partial groups are treated exactly like plausible groups: they
    // follow whatever decision the user makes on the plausible step (import/skip),
    // instead of being silently auto-submitted regardless of that choice.
    // A group containing an implausible row must never qualify here — those rows
    // still need to go through the implausible review/warning flow.
    const isNonStrictPartial = (g: ImportGroup) =>
        g.category === 'partial' &&
        !g.rows.some((r) => r.task?.readingValueValidation?.strictValidation === true) &&
        !g.rows.some((r) => r.category === 'implausible');

    // Auto-imports non-strict partial groups and marks them submitted. Only used
    // in the true fast path (a file with nothing to review at all) — everywhere
    // else, partial groups go through handlePlausibleImport.
    const autoImportPartial = (currentGroups: ImportGroup[]): ImportGroup[] => {
        const toImport = currentGroups.filter(isNonStrictPartial);
        toImport.forEach((g) => enqueueGroup(g, combineReadingHints(g.rows)));
        return applyDecision(currentGroups, new Set(toImport.map((g) => g.jobId)), 'submitted');
    };

    const goToImplausible = (currentGroups: ImportGroup[]) => {
        setGroups(currentGroups);
        const currentImplausibleGroups = currentGroups.filter(needsImplausibleReview);
        if (currentImplausibleGroups.length === 0) {
            finishSummary(currentGroups);
            return;
        }
        const noRequirements = currentImplausibleGroups.every((group) => {
            const requirements = getEffectiveRequirements(group);
            return !requirements.requireComment && !requirements.requirePhoto;
        });
        if (noRequirements) {
            setStep('IMPLAUSIBLE_WARNING');
        } else {
            startReview(currentImplausibleGroups);
        }
    };

    const handlePlausibleImport = () => {
        const toImport = groups.filter((g) => g.category === 'plausible' || isNonStrictPartial(g));
        toImport.forEach((g) => enqueueGroup(g, combineReadingHints(g.rows)));
        goToImplausible(applyDecision(groups, new Set(toImport.map((g) => g.jobId)), 'submitted'));
    };

    const startReview = (queue = implausibleGroups) => {
        if (queue.length === 0) {
            finishSummary(groups);
            return;
        }
        setReviewQueue(queue);
        setReviewIndex(0);
        setCurrentReview({ group: queue[0], comment: combineReadingHints(queue[0].rows), images: {} });
        setStep('IMPLAUSIBLE_REVIEW');
    };

    const handleWarningImportAll = () => {
        implausibleGroups.forEach((g) => enqueueGroup(g, combineReadingHints(g.rows)));
        finishSummary(applyDecision(groups, new Set(implausibleGroups.map((g) => g.jobId)), 'submitted'));
    };

    const handleReviewImport = () => {
        if (!currentReview) return;
        try {
            enqueueGroup(currentReview.group, currentReview.comment, currentReview.images);
        } catch (err) {
            if (err instanceof InvalidReadingImagePayloadError) {
                dispatch(
                    notificationActions.showErrorNotification(
                        t('jobs.details.notifications.imageFormatUnsupported.title'),
                        t('jobs.details.notifications.imageFormatUnsupported.message'),
                    ),
                );
                return;
            }

            throw err;
        }
        const updated = applyDecision(groups, new Set([currentReview.group.jobId]), 'submitted');
        setGroups(updated);
        advanceReview(updated);
    };

    // Used when a mixed group (plausible + implausible readings of the same
    // job) can't fully pass review (missing photo/comment on the implausible
    // reading) — imports only the plausible readings and marks the
    // implausible ones as skipped, instead of blocking or discarding both.
    const handleReviewImportPlausibleOnly = () => {
        if (!currentReview) return;
        const { group } = currentReview;
        const plausibleRows = group.rows.filter((r) => r.category === 'plausible');
        try {
            enqueueRows(group.jobId, plausibleRows, combineReadingHints(plausibleRows));
        } catch (err) {
            if (err instanceof InvalidReadingImagePayloadError) {
                dispatch(
                    notificationActions.showErrorNotification(
                        t('jobs.details.notifications.imageFormatUnsupported.title'),
                        t('jobs.details.notifications.imageFormatUnsupported.message'),
                    ),
                );
                return;
            }

            throw err;
        }
        const updatedRows = group.rows.map((r) => ({ ...r, decision: r.category === 'plausible' ? ('submitted' as const) : ('skipped' as const) }));
        const updated = groups.map((g) => (g.jobId === group.jobId ? { ...g, rows: updatedRows } : g));
        setGroups(updated);
        advanceReview(updated);
    };

    const handleReviewSkip = () => {
        const updated = currentReview
            ? applyDecision(groups, new Set([currentReview.group.jobId]), 'skipped')
            : groups;
        setGroups(updated);
        advanceReview(updated);
    };

    const handleReviewSkipAll = () => {
        if (!currentReview) return;

        const remainingJobIds = new Set(reviewQueue.slice(reviewIndex).map((group) => group.jobId));
        finishSummary(applyDecision(groups, remainingJobIds, 'skipped'));
    };

    const advanceReview = (currentGroups: ImportGroup[]) => {
        const next = reviewIndex + 1;
        if (next >= reviewQueue.length) {
            finishSummary(currentGroups);
        } else {
            setReviewIndex(next);
            setCurrentReview({ group: reviewQueue[next], comment: combineReadingHints(reviewQueue[next].rows), images: {} });
        }
    };

    // Single source of truth for the summary: derives every counter from the final
    // group decisions in one pass, with Σ === total. Undecided importable rows count
    // as skipped, so no row is ever lost or double-counted.
    const finishSummary = (finalGroups: ImportGroup[], total = totalRows) => {
        setGroups(finalGroups);
        setStats(computeImportStats(finalGroups, total));
        setStep('SUMMARY');
    };

    const handleClose = () => {
        onClose();
    };

    // Single source of truth for clearing everything that belongs to ONE
    // import run: groups/stats/review state, the file buffers kept for
    // re-export, and — critically — importedRowsByFile. That map is built via
    // markRowsResolved's `new Map(prev)` merge, so without an explicit reset
    // here it would keep accumulating entries across runs; since _fileIndex is
    // assigned per-run starting at 0, a second run's file 0 would collide with
    // leftover resolved-row entries from the first run's file 0.
    const resetImportRun = useCallback(() => {
        setParseError(null);
        setGroups([]);
        setTotalRows(0);
        setStats(INITIAL_STATS);
        setReviewQueue([]);
        setReviewIndex(0);
        setCurrentReview(null);
        setSkippedFileCount(0);
        setFileBuffers([]);
        setImportedRowsByFile(new Map());
        setReExporting(false);
    }, []);

    // Reset only once the close transition has fully finished, so the user
    // never sees the dialog flash back to its initial state while closing.
    const handleClosed = () => {
        setStep('FILE_SELECT');
        resetImportRun();
    };

    // Lets the user start a further import run from the summary screen
    // without closing and reopening the dialog — the reported bug was that
    // this path didn't exist at all, so leftover state from the previous run
    // (skipped-file count, re-export buffers, resolved-row tracking) had
    // nowhere to be cleared before the next file was analyzed.
    const handleImportAnother = () => {
        resetImportRun();
        setStep('FILE_SELECT');
    };

    const handleRegisterFileSelectTrigger = useCallback((trigger: () => void) => {
        setFileSelectTrigger(() => trigger);
    }, []);

    // A re-export is only useful when the re-exported file would actually
    // differ from the original — i.e. SOME rows were excluded (submitted this
    // session, or already done from an earlier import) AND genuine problem
    // rows remain to fix. `stats.submitted > 0` alone used to gate this, which
    // hid the button whenever nothing NEW was submitted but a lot of rows were
    // already done (see markRowsResolved) — exactly the case where re-export
    // (stripping the already-done rows) is most useful.
    const hasResolvedRows = stats.submitted > 0 || stats.alreadyDone > 0;
    const hasNonImportedFiles =
        hasResolvedRows &&
        (stats.skipped > 0 ||
            stats.implausibleSkipped > 0 ||
            stats.notFound > 0 ||
            stats.noValue > 0 ||
            stats.duplicateConflict > 0 ||
            stats.duplicateExact > 0 ||
            stats.invalidReadingDate > 0 ||
            stats.invalidReading > 0 ||
            stats.dateMismatch > 0 ||
            stats.expired > 0 ||
            stats.notYet > 0);

    const handleReExport = async () => {
        setReExporting(true);
        try {
            await reExportOriginalFiles(fileBuffers, importedRowsByFile);
        } catch (_e) {
            dispatch(
                notificationActions.showErrorNotification(
                    t('management.import.summary.reExportError.title'),
                    t('management.import.summary.reExportError.message'),
                ),
            );
        } finally {
            setReExporting(false);
        }
    };

    // ─── Render helpers ──────────────────────────────────────────────────────────

    const renderContent = () => {
        switch (step) {
            case 'FILE_SELECT':
                return (
                    <FileSelectStep
                        onFileSelected={handleFilesSelected}
                        onSkippedFiles={setSkippedFileCount}
                        initialError={parseError ?? undefined}
                        onTriggerFileSelect={handleRegisterFileSelectTrigger}
                    />
                );
            case 'ANALYZING':
                return (
                    <div className="p-8 flex flex-col items-center gap-3">
                        <div className="text-primary">
                            <Spinner size={32} />
                        </div>
                        <p className="text-sm text-gray-500">{t('management.import.fileSelect.analyzing')}</p>
                    </div>
                );
            case 'PLAUSIBLE_STEP':
                return <PlausibleStep groups={groups} totalRows={totalRows} />;
            case 'IMPLAUSIBLE_WARNING':
                return <ImplausibleWarningStep count={implausibleGroups.reduce((sum, group) => sum + group.rows.filter((r) => r.category === 'implausible').length, 0)} />;
            case 'IMPLAUSIBLE_REVIEW':
                if (!currentReview) return null;
                return (
                    <ImplausibleReviewStep
                        entry={currentReview}
                        currentIndex={reviewIndex}
                        total={reviewQueue.length}
                        requireComment={requireCommentForGroup}
                        requirePhoto={requirePhotoForGroup}
                        mergedConfig={jobMergedConfig}
                        onChange={setCurrentReview}
                    />
                );
            case 'SUMMARY':
                return <SummaryStep stats={stats} skippedFileCount={skippedFileCount} />;
        }
    };

    const renderFooter = () => {
        switch (step) {
            case 'FILE_SELECT':
                return (
                    <div className="flex justify-end gap-2 w-full">
                        <Button primary onClick={() => fileSelectTrigger?.()}>
                            {t('management.import.fileSelect.button')}
                        </Button>
                    </div>
                );
            case 'ANALYZING':
                return <></>;
            case 'PLAUSIBLE_STEP':
                return (
                    <div className="flex justify-end gap-2 w-full">
                        <Button primary onClick={handlePlausibleImport} disabled={plausibleCount === 0}>
                            {t('management.import.actions.import')}
                        </Button>
                    </div>
                );
            case 'IMPLAUSIBLE_WARNING':
                return (
                    <div className="flex justify-end gap-2 w-full flex-wrap">
                        <Button ghost onClick={handleWarningImportAll}>
                            {t('management.import.actions.importAll')}
                        </Button>
                        <Button primary onClick={() => startReview()}>
                            {t('management.import.actions.review')}
                        </Button>
                    </div>
                );
            case 'IMPLAUSIBLE_REVIEW':
                return (
                    <div className="flex justify-end gap-2 w-full flex-wrap">
                        <Button ghost onClick={handleReviewSkipAll}>
                            {t('management.import.actions.skipAll')}
                        </Button>
                        <div className="flex justify-end gap-2 flex-wrap">
                            <Button ghost onClick={handleReviewSkip}>
                                {t('management.import.actions.skip')}
                            </Button>
                            {!canImportReview && hasPlausibleRowInReview ? (
                                <PopConfirmButton
                                    ghost
                                    type={ConfirmContentType.WARNING}
                                    title={t('management.import.review.plausibleOnlyConfirm.title')}
                                    content={t('management.import.review.plausibleOnlyConfirm.content')}
                                    okText={t('management.import.review.plausibleOnlyConfirm.confirm')}
                                    onClick={handleReviewImportPlausibleOnly}
                                >
                                    {t('management.import.actions.import')}
                                </PopConfirmButton>
                            ) : (
                                <Button primary onClick={handleReviewImport} disabled={!canImportReview}>
                                    {t('management.import.actions.import')}
                                </Button>
                            )}
                        </div>
                    </div>
                );
            case 'SUMMARY':
                return (
                    <div className="flex justify-end gap-2 w-full flex-wrap">
                        <div className="flex gap-2 flex-wrap">
                            {hasNonImportedFiles && (
                                <Button ghost onClick={handleReExport} disabled={reExporting}>
                                    {reExporting ? (
                                        <Spinner size={16} />
                                    ) : (
                                        t('management.import.summary.reExportButton')
                                    )}
                                </Button>
                            )}
                            <Button ghost onClick={handleImportAnother} disabled={reExporting}>
                                {t('management.import.actions.importMore')}
                            </Button>
                        </div>
                        <Button primary onClick={handleClose} disabled={reExporting}>
                            {t('management.import.actions.close')}
                        </Button>
                    </div>
                );
        }
    };

    const wizardStepLabels = [
        t('management.import.wizardSteps.fileSelect'),
        t('management.import.wizardSteps.review'),
        t('management.import.wizardSteps.summary'),
    ];

    return (
        <WizardDialog
            title={t('management.import.fileSelect.title')}
            steps={wizardStepLabels}
            currentStep={toWizardStep(step)}
            visible={visible}
            onClose={step === 'ANALYZING' ? () => {} : handleClose}
            onClosed={handleClosed}
            footer={renderFooter()}
            size="medium"
        >
            {renderContent()}
        </WizardDialog>
    );
};
